import { z } from 'zod'
import { CARESTACK_PRACTICES_QUERY } from '../graphql'
import { SAVE_CARESTACK_INTEGRATION } from '../ops-graphql'
import { PmsWorkspaceWizard, type PmsWizardConfig } from './PmsWorkspaceWizard'
import type { LocationType } from 'app/types/entities'
import { gqlQuery } from 'app/lib/graphqlClient'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'

const config: PmsWizardConfig = {
  name: `Carestack`,
  setupAction: `/api/integrations/setup/carestack`,
  fields: [
    { name: `accountId`, label: `Account ID` },
    { name: `accountKey`, label: `Account Key` },
    { name: `accountUrl`, label: `Account URL` },
  ],
  schema: z.object({
    accountId: z.string().min(1, { message: `Account ID is required` }),
    accountKey: z.string().min(1, { message: `Account Key is required` }),
    accountUrl: z.string().min(1, { message: `Account URL is required` }),
  }),
  loadErrorMessage: `Could not load locations from Carestack. Confirm the Carestack credentials, then try again.`,
  loadRemoteEntities: async () => {
    const result = await gqlQuery(CARESTACK_PRACTICES_QUERY, {}, { suppressErrorToast: true })
    const entities = result.data?.integrationOptions.carestackPractices
    return entities ? { entities } : { errorMessage: result.error?.message }
  },
  saveMapping: async (selection, locationId) => {
    const result = await gqlOpsMutation(SAVE_CARESTACK_INTEGRATION, { locationId, selection })
    const saved = result.data?.saveCarestackIntegration
    return { success: !!saved?.success, error: saved?.error ?? result.error }
  },
  isLocationLinked: (location: LocationType, remoteId: string) => location.carestackId?.split(`-`)[1] === remoteId,
  isLocationUsed: (location: LocationType) => !!location.carestackId,
}

export function CarestackWorkspaceModalForm({
  onIntegrationChanged,
  currentValues,
  onBack,
}: {
  onIntegrationChanged: () => void
  currentValues?: {
    accountId?: string
    accountKeySet?: boolean
    accountUrl?: string
  }
  /** Wizard back target: overrides returning to the workspace integration picker. */
  onBack?: () => void
}) {
  return (
    <PmsWorkspaceWizard
      config={config}
      onIntegrationChanged={onIntegrationChanged}
      currentValues={currentValues}
      onBack={onBack}
    />
  )
}
